import {
  BracketGraph,
  getChildrenNodesByDepth,
  getsLosingTeam,
} from "./BracketGraph";
import { getStringPick, Pick } from "model/Pickem/Pickem";
import {
  Bracket,
  getBracketMatchIds,
} from "model/Tournament/Segment/Bracket/Bracket";
import { isSeedSource } from "./BracketMatch";
import { RootState } from "app/store";
import { selectMatch } from "selectors/Match";
import { getRosterId, getRosterIdOrPlaceholder } from "model/Roster/Roster";

export type BracketPickOption = {
  matchId: string;
  rosterOneId: string | undefined;
  rosterTwoId: string | undefined;
};

export const selectBracketPickOptions = (
  state: RootState,
  bracket: Bracket,
  picks: { [matchId: string]: Pick }
) => {
  const graph = bracket.graph as BracketGraph;
  if (!graph) {
    return [];
  }

  const bracketMatchIds = getBracketMatchIds(bracket);
  const pickOptions: BracketPickOption[] = [];
  bracketMatchIds.forEach((id) =>
    pickOptions.push({
      matchId: id,
      rosterOneId: undefined,
      rosterTwoId: undefined,
    })
  );

  const { upperNodesByDepth, lowerNodesByDepth } = getChildrenNodesByDepth(
    graph.finalNodes
  );

  const upperNodesByDepthWithFinals =
    graph.finalNodes.length > 1
      ? [graph.finalNodes.filter((n) => !n.isLower), ...upperNodesByDepth]
      : upperNodesByDepth;

  for (let i = upperNodesByDepthWithFinals.length - 1; i >= 0; i--) {
    const upperNodesAtDepth = upperNodesByDepthWithFinals[i];
    for (let j = 0; j < upperNodesAtDepth.length; j++) {
      const node = upperNodesAtDepth[j];
      if (!node) {
        continue;
      }

      const matchId = node.match.matchId;
      const pickOption = pickOptions.find((n) => n.matchId === matchId);
      if (!pickOption) {
        continue;
      }

      const match = selectMatch(state, matchId);
      if (!match) {
        continue;
      }

      if (isSeedSource(node.match.teamOneSource)) {
        const rosterOne = match.teams.rosters.at(0);
        const rosterOneId = !!rosterOne
          ? getRosterIdOrPlaceholder(rosterOne)
          : undefined;
        pickOption.rosterOneId = rosterOneId;
      } else {
        const pick = getStringPick(picks, node.match.teamOneSource);
        pickOption.rosterOneId = pick;
      }

      if (isSeedSource(node.match.teamTwoSource)) {
        const rosterTwo = match.teams.rosters.at(1);
        const rosterTwoId = !!rosterTwo
          ? getRosterIdOrPlaceholder(rosterTwo)
          : undefined;
        pickOption.rosterTwoId = rosterTwoId;
      } else {
        const pick = getStringPick(picks, node.match.teamTwoSource);
        pickOption.rosterTwoId = pick;
      }
    }
  }

  const lowerNodesByDepthWithFinals =
    graph.finalNodes.length > 1
      ? [graph.finalNodes.filter((n) => n.isLower), ...lowerNodesByDepth]
      : lowerNodesByDepth;

  for (let i = lowerNodesByDepthWithFinals.length - 1; i >= 0; i--) {
    const lowerNodesAtDepth = lowerNodesByDepthWithFinals[i];
    for (let j = 0; j < lowerNodesAtDepth.length; j++) {
      const node = lowerNodesAtDepth[j];
      if (!node) {
        continue;
      }

      const matchId = node.match.matchId;
      const pickOption = pickOptions.find((n) => n.matchId === matchId);
      if (!pickOption) {
        continue;
      }

      const match = selectMatch(state, matchId);
      if (!match) {
        continue;
      }

      if (isSeedSource(node.match.teamOneSource)) {
        const rosterOne = match.teams.rosters.at(0);
        const rosterOneId = !!rosterOne
          ? getRosterIdOrPlaceholder(rosterOne)
          : undefined;
        pickOption.rosterOneId = rosterOneId;
      } else {
        const pick = getStringPick(picks, node.match.teamOneSource);
        if (!!pick) {
          if (getsLosingTeam(node, node.teamOneSource)) {
            const sourceOption = pickOptions.find(
              (o) => o.matchId === node.match.teamOneSource
            );
            const otherRosterId = !!sourceOption
              ? getOtherRosterId(pick, sourceOption)
              : undefined;
            pickOption.rosterOneId = otherRosterId;
          } else {
            pickOption.rosterOneId = pick;
          }
        }
      }

      if (isSeedSource(node.match.teamTwoSource)) {
        const rosterTwo = match.teams.rosters.at(1);
        const rosterTwoId = !!rosterTwo
          ? getRosterIdOrPlaceholder(rosterTwo)
          : undefined;
        pickOption.rosterTwoId = rosterTwoId;
      } else {
        const pick = getStringPick(picks, node.match.teamTwoSource);
        if (!!pick) {
          if (getsLosingTeam(node, node.teamTwoSource)) {
            const sourceOption = pickOptions.find(
              (o) => o.matchId === node.match.teamTwoSource
            );
            const otherRosterId = !!sourceOption
              ? getOtherRosterId(pick, sourceOption)
              : undefined;
            pickOption.rosterTwoId = otherRosterId;
          } else {
            pickOption.rosterTwoId = pick;
          }
        }
      }
    }
  }

  const gfNode =
    graph.finalNodes.length === 1 ? graph.finalNodes[0] : undefined;
  if (!!gfNode) {
    const matchId = gfNode.match.matchId;
    const pickNode = pickOptions.find((n) => n.matchId === matchId);
    if (!!pickNode) {
      const match = selectMatch(state, matchId);
      if (!!match) {
        if (isSeedSource(gfNode.match.teamOneSource)) {
          const rosterOne = match.teams.rosters.at(0);
          const rosterOneId = !!rosterOne ? getRosterId(rosterOne) : undefined;
          pickNode.rosterOneId = rosterOneId;
        } else {
          const pick = getStringPick(picks, gfNode.match.teamOneSource);
          pickNode.rosterOneId = pick;
        }

        if (isSeedSource(gfNode.match.teamTwoSource)) {
          const rosterTwo = match.teams.rosters.at(1);
          const rosterTwoId = !!rosterTwo ? getRosterId(rosterTwo) : undefined;
          pickNode.rosterTwoId = rosterTwoId;
        } else {
          const pick = getStringPick(picks, gfNode.match.teamTwoSource);
          pickNode.rosterTwoId = pick;
        }
      }
    }
  }

  return pickOptions;
};

const getOtherRosterId = (rosterId: string, pickOption: BracketPickOption) => {
  if (pickOption.rosterOneId === rosterId) {
    return pickOption.rosterTwoId;
  }

  return pickOption.rosterOneId;
};
